'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BuildingsIcon,
  FilesIcon,
  QuestionIcon,
  ShieldWarningIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { Combobox } from '@tranquility-ai/theme-components/combobox';
import { Button } from '@tranquility-ai/theme-components/button';
import { Popover as TpPopover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { CjisLogo } from '@tranquility-ai/theme-components/cjis-logo';
import { AuthContext, FeaturesContext, StatsContext } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { useUserFlaggedAttentionCount } from '@/hooks/useUserFlaggedAttentionCount';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import {
  CaptureError,
  FetchWithVersionCheck,
  getTokenInfo,
  recordStat,
} from '@/shared/functions';
import { isWorkProductView } from '@/shared/types';
import { isAdHocCase } from '@/shared/ad-hoc-evidence';
import { HotKeys } from '../hot-keys';
import UploadersMenu, { UploadersModal, useUploaderTargets } from '../uploaders-menu';
import { NewCaseModal } from '@/components/new-case-modal';
import { useCaseCreationFlowsEnabled } from '@/shared/case-creation-flag';
import { caseNameFromPickerValue, casePickerExtraOptions } from '@/shared/create-case-flow';
import { PrimaryNavAction, PrimaryNavExternalAction } from './PrimaryNavAction';
import { PrimaryNavBrand } from './PrimaryNavBrand';
import { PrimaryNavMenu } from './PrimaryNavMenu';
import {
  AccountMenuItem,
  AssistantMenuItem,
  CourtroomMenuItem,
  CsamMenuItem,
  DetailsMenuItem,
  FeedbackButton,
  HelpMenuItem,
  HotkeysMenuItem,
  LogoutMenuItem,
  PresentationMenuItem,
  SyncMenuItem,
  UploadMenuItem,
  WorkProductsMenuItem,
} from './nav-menu-items';
import { useCasePlaceholder, usePrimaryNavCases } from './use-primary-nav-cases';
import { usePrimaryNavOverflow } from './use-primary-nav-overflow';
import type { PrimaryNavigationProps, TimelineDropdownItem } from './types';
import { LanguageSelect } from '@/i18n/LanguageSelect';
import { useI18n } from '@/i18n/LocaleProvider';
import { useLanguageMenuEnabled } from '@/i18n/language-menu-flag';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { isMobilePrimaryNav } from './constants';
import './primary-navigation.css';

const HotkeysModal = dynamic(() => import('../hotkeys-modal'), { ssr: false, loading: () => null });
const AdHocFileConflictModal = dynamic(
  () => import('@/components/ad-hoc-file-conflict-modal').then((m) => m.AdHocFileConflictModal),
  { ssr: false, loading: () => null },
);

export function PrimaryNavigation({
  className = '',
  selectedOption,
  onOptionChange,
  selectedSecondOption: _selectedSecondOption,
  onSecondOptionChange: _onSecondOptionChange,
  userId,
  onViewChange,
  selectedView,
  onQueryKeyChange: _onQueryKeyChange,
  newTimelineIsAvailable,
  multipleTimelinesAvailable,
  loadingTimeline: _loadingTimeline,
  clientWidth,
  onFeedbackClick,
  onCreateTimeline: _onCreateTimeline,
  creatingTimeline: _creatingTimeline,
  timelineCreated: _timelineCreated,
  timelineServiceAppearsOperational: _timelineServiceAppearsOperational,
  currentTimelineId: _currentTimelineId,
  docIdMode: _docIdMode,
  setTenantName: _setTenantName,
  setRemoteStorageLink: _setRemoteStorageLink,
  setOrgName,
  onWorkProductsClick,
  setBanners: _setBanners,
  tenantId = '',
  username = '',
  adHocFileUpload,
  handleLogout,
  isUserAdmin,
  csamAccessReady = true,
}: PrimaryNavigationProps) {
  const t = useTimePilotT();
  const { enabledLocaleOptions } = useI18n();
  const languageMenuEnabled = useLanguageMenuEnabled('timepilot');
  const showLanguageControl = languageMenuEnabled && enabledLocaleOptions.length > 1;
  const [isSyncing, setIsSyncing] = useState(false);
  const [showHotkeysModal, setShowHotkeysModal] = useState(false);
  const [, setLastSelectedView] = useState<string | null>(null);
  const [, setWasClicked] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCjisPopoverVisible, setIsCjisPopoverVisible] = useState(false);
  const [timelinesList, setTimelinesList] = useState<TimelineDropdownItem[]>([]);
  const documentIdInputRef = useRef<HTMLInputElement>(null);

  const { featureFlags } = useContext(FeaturesContext) || {};
  const statsContext = useContext(StatsContext);
  const { idToken, accessToken, tenantName: authTenantName } = useContext(AuthContext);

  const cellId = useMemo(() => resolveCellIdFromIdToken(idToken) ?? '', [idToken]);
  const tenantPrefix =
    authTenantName && authTenantName.trim() !== '' ? authTenantName.trim() : undefined;

  const caseCreationEnabled = useCaseCreationFlowsEnabled();
  const [caseQuery, setCaseQuery] = useState('');
  const [newCase, setNewCase] = useState<{ name: string } | null>(null);
  const uploaderTargets = useUploaderTargets({
    featureFlags,
    caseId: selectedOption ?? undefined,
    tenantId,
    username,
    cellId,
  });
  const hasImportOptions = uploaderTargets.length > 0 || Boolean(adHocFileUpload);
  const { canOpenDashboard } = useCsamAccess();
  const { isCaseListLoading, caseComboboxOptions, caseNameOptions, personalFilesCaseId } = usePrimaryNavCases({
    userId,
    selectedOption,
    onOptionChange,
    featureFlags,
  });
  const showCsamNav =
    Boolean(canOpenDashboard && selectedOption && !isAdHocCase(selectedOption, personalFilesCaseId));
  const { data: userFlaggedAttentionCount } = useUserFlaggedAttentionCount(
    showCsamNav ? selectedOption : null,
  );
  const csamBadgeCount =
    (userFlaggedAttentionCount ?? 0) > 0 ? userFlaggedAttentionCount : undefined;

  const clearQueryTimer = useRef<number | null>(null);
  const clearCaseQuery = useCallback(() => {
    if (clearQueryTimer.current != null) window.clearTimeout(clearQueryTimer.current);
    clearQueryTimer.current = null;
    setCaseQuery('');
  }, []);
  // The list is portaled, so clicking "Create case …" blurs this wrapper before
  // the option's click. Clearing the query in that blur removes the row first.
  // Defer the clear until after the click.
  const scheduleClearCaseQuery = useCallback(() => {
    if (clearQueryTimer.current != null) window.clearTimeout(clearQueryTimer.current);
    clearQueryTimer.current = window.setTimeout(() => {
      clearQueryTimer.current = null;
      setCaseQuery('');
    }, 0);
  }, []);
  useEffect(() => () => {
    if (clearQueryTimer.current != null) window.clearTimeout(clearQueryTimer.current);
  }, []);

  const openNewCase = useCallback((name = '') => {
    clearCaseQuery();
    setNewCase({ name });
  }, [clearCaseQuery]);

  const pickerOptions = useMemo(() => {
    if (!caseCreationEnabled) return caseComboboxOptions;
    const extras = casePickerExtraOptions(caseQuery, caseNameOptions);
    return [...extras, ...caseComboboxOptions];
  }, [caseComboboxOptions, caseCreationEnabled, caseNameOptions, caseQuery]);

  const handleOptionChange = useCallback(
    (value: string | undefined) => {
      if (value && caseCreationEnabled) {
        const create = caseNameFromPickerValue(value);
        if (create?.kind === 'new') {
          openNewCase();
          return;
        }
        if (create?.kind === 'named') {
          openNewCase(create.name);
          return;
        }
      }
      sessionStorage.setItem('queryKey', '');
      if (documentIdInputRef?.current) {
        documentIdInputRef.current.value = '';
      }
      onOptionChange(value ?? undefined);
    },
    [caseCreationEnabled, onOptionChange, openNewCase],
  );

  const handleSyncData = useCallback(async () => {
    if (!selectedOption) {
      alert(t('nav.syncSelectCase'));
      return;
    }
    setIsSyncing(true);
    try {
      const result = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId, selectedCase: selectedOption }),
      });
      if (result.ok) {
        alert(t('nav.syncStarted'));
      } else {
        const errorMessage = `Failed to sync data: ${result.statusText}`;
        CaptureError(errorMessage, new Error(errorMessage));
        alert(t('nav.syncFailed', { status: result.statusText }));
      }
    } catch (error) {
      const errorMessage = `Error syncing data for user ${userId}: ${error}`;
      CaptureError(errorMessage, error as Error);
      alert(t('nav.syncError'));
    } finally {
      setIsSyncing(false);
    }
  }, [userId, selectedOption, accessToken, t]);

  const handleViewChange = useCallback(
    (view: Parameters<typeof onViewChange>[0]) => {
      onViewChange(view);
    },
    [onViewChange],
  );

  const menuItems = useMemo(
    () => ({
      Feedback: <FeedbackButton onFeedbackClick={onFeedbackClick} />,
      Account: isUserAdmin ? (
        <AccountMenuItem
          selected={selectedView === 'account'}
          onSelect={() => handleViewChange('account')}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ) : null,
      Sync: <SyncMenuItem handleSyncData={handleSyncData} isSyncing={isSyncing} />,
      Hotkeys: (
        <HotkeysMenuItem
          setShowHotkeysModal={setShowHotkeysModal}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ),
      Details: (
        <DetailsMenuItem
          handleViewChange={handleViewChange}
          setLastSelectedView={setLastSelectedView}
          setWasClicked={setWasClicked}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ),
      Presentation: (
        <PresentationMenuItem
          handleViewChange={handleViewChange}
          setLastSelectedView={setLastSelectedView}
          setWasClicked={setWasClicked}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ),
      Courtroom: featureFlags?.courtroomMode ? (
        <CourtroomMenuItem
          handleViewChange={handleViewChange}
          setLastSelectedView={setLastSelectedView}
          setWasClicked={setWasClicked}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ) : null,
      Assistant: (
        <AssistantMenuItem
          featureFlags={featureFlags}
          selectedView={selectedView}
          handleViewChange={handleViewChange}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ),
      WorkProducts: (
        <WorkProductsMenuItem
          featureFlags={featureFlags}
          selectedView={selectedView}
          handleViewChange={handleViewChange}
          onWorkProductsClick={onWorkProductsClick}
          onCloseMenu={() => setIsOverflowOpen(false)}
        />
      ),
      Upload: hasImportOptions ? (
        <UploadMenuItem
          onOpen={() => {
            setIsOverflowOpen(false);
            setIsUploadModalOpen(true);
          }}
        />
      ) : null,
      Csam: showCsamNav ? (
        <CsamMenuItem
          selected={selectedView === 'csam-report'}
          onSelect={() => handleViewChange('csam-report')}
          onCloseMenu={() => setIsOverflowOpen(false)}
          badgeCount={csamBadgeCount}
        />
      ) : null,
      Help: <HelpMenuItem onCloseMenu={() => setIsOverflowOpen(false)} />,
    }),
    [
      featureFlags,
      selectedView,
      handleSyncData,
      isSyncing,
      handleViewChange,
      onWorkProductsClick,
      onFeedbackClick,
      hasImportOptions,
      showCsamNav,
      csamBadgeCount,
      isUserAdmin,
    ],
  );

  const { overflowLevel, overflowMenuKeys } = usePrimaryNavOverflow({
    clientWidth,
    menuItems,
    selectedOption,
  });

  const casePlaceholder = useCasePlaceholder(overflowLevel);

  const isWideViewport = clientWidth !== undefined && clientWidth >= 500;
  const isMobileNav = isMobilePrimaryNav(clientWidth);
  const showNavMenu = isMobileNav || overflowLevel > 0 || !isWideViewport;

  // Shared by the bar's CJIS trigger and the menu panel's footer — only one renders.
  const cjisPopoverContent = (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2 border-b-[3px] border-double border-black pb-4">
        <CjisLogo
          size="md"
          src="/cjis.png"
          className={`${isCjisPopoverVisible ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'} hover:cursor-help hover:opacity-100 hover:grayscale-0`}
        />
        <h3 className="text-[22px] font-normal text-[var(--tp-popover-fg)]">
          {t('nav.cjisTitle')}
        </h3>
      </div>
      <p className="text-[18px] italic leading-normal text-[var(--tp-popover-fg)]">
        {t('nav.cjisBody')}
      </p>
      <ul className="m-0 ml-8 list-disc p-0 text-[14px] text-[var(--tp-popover-fg)]">
        <li className="mb-4">{t('nav.cjisStandards')}</li>
        <li>{t('nav.cjisAuditTrail')}</li>
      </ul>
    </div>
  );

  const showLeftAssistant = selectedOption && isWideViewport && overflowLevel < 2.5;
  const showLeftWorkProducts = selectedOption && isWideViewport && overflowLevel < 2.5;
  const showLeftCsam = showCsamNav && isWideViewport && overflowLevel < 0.5;
  const showLeftImport = hasImportOptions && isWideViewport && overflowLevel < 0.5;
  const showRightAccount = isUserAdmin && isWideViewport && overflowLevel < 1;
  const showRightHelp = isWideViewport && overflowLevel < 0.5;

  useEffect(() => {
    if (!idToken) return;
    try {
      const info = getTokenInfo(idToken) as Record<string, unknown> & {
        payload?: Record<string, unknown>;
      };
      const org =
        (info?.payload?.['custom:org_name'] as string | undefined) ??
        (info?.['custom:org_name'] as string | undefined);
      if (org && org.trim() !== '') {
        setOrgName(org);
      }
    } catch {
      /* ignore malformed token */
    }
  }, [idToken, setOrgName]);

  useEffect(() => {
    if (tenantPrefix) {
      document.title = `TimePilot ${tenantPrefix.toUpperCase()}`;
    }
  }, [tenantPrefix]);

  useEffect(() => {
    if (multipleTimelinesAvailable) {
      setTimelinesList(
        multipleTimelinesAvailable.sort((a, b) => {
          if (a.createdBy === userId) return -1;
          if (b.createdBy === userId) return 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }),
      );
    }
  }, [multipleTimelinesAvailable, userId]);

  return (
    <nav
      id="main-navigation"
      role="navigation"
      aria-label={t('nav.mainNavigation')}
      tabIndex={-1}
      data-csam-access-ready={csamAccessReady ? 'true' : 'false'}
      className={`tp-primary-navigation fixed top-0 z-[99999] box-border flex w-screen flex-row items-center justify-between font-inter text-titlebar-button-fg ${className}`}
    >
      <div className="tp-primary-navigation__cluster min-w-0 max-w-full">
        <PrimaryNavBrand
          tenantPrefix={tenantPrefix}
          userId={userId}
          helioTraceEnabled={featureFlags?.helioTraceAccess === 'enabled'}
        />
        <div
          className="tp-primary-navigation__case-selector flex min-w-0 items-center gap-2 font-inter"
          onInput={(event) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement)) return;
            const selectedLabel =
              caseComboboxOptions.find((option) => option.value === selectedOption)?.label ?? '';
            if (target.value === selectedLabel) clearCaseQuery();
            else {
              if (clearQueryTimer.current != null) window.clearTimeout(clearQueryTimer.current);
              clearQueryTimer.current = null;
              setCaseQuery(target.value);
            }
          }}
          onBlur={scheduleClearCaseQuery}
        >
          <Combobox
            aria-label={t('nav.selectCase')}
            variant="navPill"
            loading={isCaseListLoading}
            loadingText={t('nav.loadingCases')}
            value={selectedOption ?? null}
            onValueChange={handleOptionChange}
            options={pickerOptions}
            placeholder={casePlaceholder}
            emptyMessage={t('nav.noMatchingCases')}
          />
        </div>
        <div className="tp-primary-navigation__actions hidden sm:flex">
          {showLeftAssistant && (
            <PrimaryNavAction
              id="AssistantMenuItem"
              label={t('nav.assistant')}
              selected={selectedView === 'chat'}
              ariaLabel={t('nav.openAssistant')}
              icon={
                <Image
                  src={
                    selectedView === 'chat'
                      ? '/icons/assistant-small-white.svg'
                      : '/icons/assistant-small-light-white.svg'
                  }
                  alt=""
                  width={16}
                  height={16}
                  priority
                  className="size-4"
                />
              }
              onClick={(event) => {
                handleViewChange('chat');
                recordStat('OpenAssistantChatButtonClicked', event, statsContext);
              }}
            />
          )}
          {showLeftWorkProducts && (
            <PrimaryNavAction
              id="WorkProductsMenuItem"
              label={t('nav.workProducts')}
              selected={isWorkProductView(selectedView)}
              ariaLabel={t('nav.openWorkProducts')}
              icon={
                <FilesIcon
                  size={16}
                  weight={isWorkProductView(selectedView) ? 'bold' : 'regular'}
                />
              }
              onClick={(event) => {
                if (onWorkProductsClick) {
                  onWorkProductsClick();
                } else {
                  handleViewChange('work-product-index');
                }
                recordStat('OpenWorkProductsButtonClicked', event, statsContext);
              }}
            />
          )}
          {showLeftCsam && (
            <PrimaryNavAction
              id="CsamButton"
              label={t('nav.csam')}
              selected={selectedView === 'csam-report'}
              ariaLabel={t('nav.openCsam')}
              badgeCount={csamBadgeCount}
              badgeTestId="csam-user-flagged-badge"
              icon={
                <ShieldWarningIcon
                  size={16}
                  weight={selectedView === 'csam-report' ? 'bold' : 'regular'}
                />
              }
              onClick={(event) => {
                handleViewChange('csam-report');
                recordStat('CsamDashboardButtonClicked', event, statsContext);
              }}
            />
          )}
          {showLeftImport && (
            <UploadersMenu
              featureFlags={featureFlags}
              caseId={selectedOption ?? undefined}
              tenantId={tenantId}
              username={username}
              cellId={cellId}
              layout="horizontal"
              adHocFileUpload={adHocFileUpload}
            />
          )}
        </div>
      </div>

      <div className="tp-primary-navigation__cluster tp-primary-navigation__cluster--end shrink-0">
        <div className="tp-primary-navigation__actions">
          {!isMobileNav && showLanguageControl && <LanguageSelect />}
          {showRightHelp && (
            <PrimaryNavExternalAction
              id="HelpButton"
              label={t('nav.help')}
              href="https://intercom.help/tranquility-ai/en/"
              icon={<QuestionIcon size={16} weight="regular" />}
              onClick={(event) => {
                recordStat('HelpButtonClicked', event, statsContext);
              }}
            />
          )}
          {showRightAccount && (
            <PrimaryNavAction
              id="AccountButton"
              label={t('nav.account')}
              selected={selectedView === 'account'}
              ariaLabel={t('nav.openAccount')}
              icon={
                <BuildingsIcon
                  size={16}
                  weight={selectedView === 'account' ? 'bold' : 'regular'}
                />
              }
              onClick={(event) => {
                handleViewChange('account');
                recordStat('AccountButtonClicked', event, statsContext);
              }}
            />
          )}
          {!isMobileNav && (
            <PrimaryNavAction
              id="LogoutButton"
              label={t('nav.logOut')}
              icon={<SignOutIcon size={16} />}
              onClick={(event) => {
                recordStat('LogoutButtonClicked', event, statsContext);
                handleLogout();
              }}
            />
          )}
          <PrimaryNavMenu
            visible={showNavMenu}
            isOpen={isOverflowOpen}
            onOpenChange={setIsOverflowOpen}
            menuKeys={overflowMenuKeys}
            menuItems={menuItems}
            utilityItems={
              isMobileNav
                ? [
                    showLanguageControl ? <LanguageSelect key="language" layout="menu" /> : null,
                    <LogoutMenuItem
                      key="logout"
                      onLogout={handleLogout}
                      onCloseMenu={() => setIsOverflowOpen(false)}
                    />,
                  ]
                : undefined
            }
            footer={
              <TpPopover
                placement="top"
                isOpen={isCjisPopoverVisible}
                onOpenChange={setIsCjisPopoverVisible}
                maxWidth="25rem"
                variant="inverted"
                content={cjisPopoverContent}
                className="tp-primary-navigation__menu-cjis"
              >
                <Button
                  type="button"
                  style="text"
                  color="light"
                  background="brand"
                  aria-label={t('nav.cjisInfo')}
                  className="tp-primary-navigation__menu-cjis-button h-auto min-h-0 border-none p-0 hover:cursor-help"
                  onClick={() => setIsCjisPopoverVisible((current) => !current)}
                >
                  <span className="tp-primary-navigation__menu-cjis-content">
                    <CjisLogo
                      size="sm"
                      src="/cjis.png"
                      className={`${isCjisPopoverVisible ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'} hover:opacity-100 hover:grayscale-0`}
                    />
                    <span className="tp-primary-navigation__menu-cjis-caption">
                      {t('nav.cjisTitle')}
                    </span>
                  </span>
                </Button>
              </TpPopover>
            }
          />
          {!showNavMenu && (
            <TpPopover
              placement="bottom-end"
              isOpen={isCjisPopoverVisible}
              onOpenChange={setIsCjisPopoverVisible}
              maxWidth="25rem"
              variant="inverted"
              content={cjisPopoverContent}
            >
              <Button
                type="button"
                style="text"
                color="light"
                background="brand"
                aria-label={t('nav.cjisInfo')}
                className="h-auto min-h-0 gap-0 border-none bg-transparent p-0 hover:cursor-help"
                onClick={() => setIsCjisPopoverVisible((current) => !current)}
              >
                <CjisLogo
                  size="sm"
                  src="/cjis.png"
                  className={`${isCjisPopoverVisible ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'} hover:opacity-100 hover:grayscale-0`}
                />
              </Button>
            </TpPopover>
          )}
        </div>
      </div>

      <HotkeysModal isOpen={showHotkeysModal} onOpenChange={setShowHotkeysModal} />
      <HotKeys onShowHotkeys={() => setShowHotkeysModal(true)} />

      {isUploadModalOpen && (
        <UploadersModal
          targets={uploaderTargets}
          onClose={() => setIsUploadModalOpen(false)}
          adHocFileUpload={adHocFileUpload}
        />
      )}
      <NewCaseModal
        isOpen={newCase != null}
        initialName={newCase?.name ?? ''}
        onClose={() => setNewCase(null)}
        onCaseCreated={(created) => {
          if (created.name === selectedOption) {
            onViewChange('chat');
            return;
          }
          onOptionChange(created.name, 'chat');
        }}
      />

      {adHocFileUpload && (
        <>
          <AdHocFileConflictModal {...adHocFileUpload.conflictModal} />
          <input
            ref={adHocFileUpload.fileInputRef}
            type="file"
            multiple
            className="hidden"
            aria-hidden
            onChange={adHocFileUpload.handleFileInputChange}
          />
        </>
      )}
    </nav>
  );
}

export default PrimaryNavigation;
